import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, BUSINESS } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in your name, email and message");
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const details = [
    { icon: Phone, label: "Call us", value: BUSINESS.phone, href: BUSINESS.phoneHref },
    { icon: Mail, label: "Auto detailing", value: BUSINESS.emailAuto, href: `mailto:${BUSINESS.emailAuto}` },
    { icon: Mail, label: "Home interiors", value: BUSINESS.emailHome, href: `mailto:${BUSINESS.emailHome}` },
    { icon: MapPin, label: "Based in", value: BUSINESS.location, href: null },
  ];

  return (
    <section id="contact" className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10" data-testid="contact-section">
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">Get in touch</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none text-foreground mb-10">
            Let&apos;s talk<br /><span className="italic text-primary">detail.</span>
          </h2>
          <p className="text-muted-foreground font-light text-lg max-w-md mb-12 leading-relaxed">
            Questions, custom quotes for your home, or fleet detailing? Reach out — we&apos;d love to bring the Platinum standard to you.
          </p>

          <div className="space-y-6">
            {details.map((d) => (
              <motion.div key={d.label} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                className="flex items-center gap-4" data-testid={`contact-detail-${d.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="w-11 h-11 border border-border flex items-center justify-center text-primary shrink-0">
                  <d.icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{d.label}</p>
                  {d.href ? (
                    <a href={d.href} className="text-foreground hover:text-primary transition-colors break-all">{d.value}</a>
                  ) : (
                    <p className="text-foreground">{d.value}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="bg-card border border-border clip-frame p-8 lg:p-10" data-testid="contact-form">
          <div className="grid gap-5">
            <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="bg-background border-border h-12" />
            <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="bg-background border-border h-12" />
            <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" className="bg-background border-border h-12" />
            <Textarea placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className="bg-background border-border min-h-36" />
            <button type="submit" disabled={sending} data-testid="contact-submit-btn"
              className="mt-2 px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame flex items-center justify-center gap-2 disabled:opacity-60">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />} Send Message
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
